import { useEffect, useRef, useState, type FormEvent } from "react";
import { ArrowLeft, ArrowRight, Eye, EyeOff, LockKeyhole, ShieldCheck } from "lucide-react";
import { api, ApiError } from "../api";

type Props = { onAuthenticated: () => void };
type PublicConfig = { turnstileRequired: boolean; turnstileSiteKey: string };

export function AdminLoginScreen({ onAuthenticated }: Props) {
  const [config, setConfig] = useState<PublicConfig>({ turnstileRequired: false, turnstileSiteKey: "" });
  const [turnstileToken, setTurnstileToken] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [pending, setPending] = useState(false);
  const [error, setError] = useState("");
  const containerRef = useRef<HTMLDivElement>(null);
  const widgetIdRef = useRef<string | null>(null);

  useEffect(() => {
    void api<PublicConfig>("/auth/config").then(setConfig).catch(() => setError("无法读取安全配置，请刷新重试"));
  }, []);

  useEffect(() => {
    if (!config.turnstileRequired || document.querySelector("script[data-turnstile]")) return;
    const script = document.createElement("script");
    script.src = "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";
    script.async = true;
    script.defer = true;
    script.dataset.turnstile = "true";
    document.head.appendChild(script);
  }, [config.turnstileRequired]);

  useEffect(() => {
    if (!config.turnstileRequired || !config.turnstileSiteKey) return;
    const script = document.querySelector<HTMLScriptElement>("script[data-turnstile]");
    const renderWidget = () => {
      if (!window.turnstile || !containerRef.current || widgetIdRef.current) return;
      widgetIdRef.current = window.turnstile.render(containerRef.current, {
        sitekey: config.turnstileSiteKey,
        action: "admin_login",
        theme: "light",
        callback: setTurnstileToken,
        "expired-callback": () => setTurnstileToken(""),
        "error-callback": () => setTurnstileToken(""),
      });
    };
    script?.addEventListener("load", renderWidget);
    renderWidget();
    return () => {
      script?.removeEventListener("load", renderWidget);
      const widgetId = widgetIdRef.current;
      widgetIdRef.current = null;
      if (widgetId && window.turnstile) window.turnstile.remove(widgetId);
      containerRef.current?.replaceChildren();
    };
  }, [config.turnstileRequired, config.turnstileSiteKey]);

  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setPending(true);
    setError("");
    const form = new FormData(event.currentTarget);
    try {
      await api("/auth/admin-login", {
        method: "POST",
        body: {
          username: form.get("username"),
          password: form.get("password"),
          turnstileToken: turnstileToken || undefined,
        },
      });
      onAuthenticated();
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : "暂时无法完成管理员登录");
    } finally {
      const widgetId = widgetIdRef.current;
      if (widgetId && window.turnstile) window.turnstile.reset(widgetId);
      setTurnstileToken("");
      setPending(false);
    }
  };

  return (
    <main className="auth-shell admin-auth-shell">
      <section className="auth-story admin-auth-story">
        <nav className="landing-nav">
          <div className="brand-lockup"><div className="brand-mark"><span>PS</span></div><span>Persona Society</span></div>
          <span className="alpha-pill">GOVERNANCE ACCESS</span>
        </nav>
        <div className="hero-copy admin-hero-copy">
          <div className="eyebrow"><ShieldCheck size={14} /> SYSTEM OPERATOR</div>
          <h1>上帝视角，<br /><em>但不是无限权力。</em></h1>
          <p className="hero-lead">治理智能体通过受控工具查询 AI Society。所有高风险动作必须先预览、再确认，并写入不可省略的审计记录。</p>
          <div className="admin-principles">
            <span>Plan → Preview → Execute</span>
            <span>PRIVATE Brain 不可访问</span>
            <span>永久动作二次确认</span>
          </div>
        </div>
        <div className="trust-note"><LockKeyhole size={16} /><span>仅已授权的 OPERATOR 账号可进入。</span></div>
      </section>

      <section className="auth-panel">
        <div className="auth-box">
          <a className="admin-back-link" href="/"><ArrowLeft size={15} /> 返回用户入口</a>
          <div className="auth-heading admin-auth-heading">
            <span>安全登录</span>
            <h2>超级管理员控制台</h2>
            <p>登录行为受速率限制与 Turnstile 双重保护。</p>
          </div>
          <form onSubmit={submit} className="auth-form">
            <label className="field"><span>管理员用户名</span><div className="input-wrap"><ShieldCheck size={17} /><input name="username" defaultValue="admin1998" minLength={4} maxLength={32} required autoComplete="username" /></div></label>
            <label className="field"><span>管理员密码</span><div className="input-wrap"><LockKeyhole size={17} /><input name="password" type={showPassword ? "text" : "password"} minLength={10} maxLength={128} required autoComplete="current-password" /><button type="button" className="icon-button" onClick={() => setShowPassword((value) => !value)} aria-label="显示或隐藏密码">{showPassword ? <EyeOff size={17} /> : <Eye size={17} />}</button></div></label>
            {config.turnstileRequired && <div ref={containerRef} />}
            {error && <div className="form-error" role="alert">{error}</div>}
            <button className="button button--primary button--wide" disabled={pending || (config.turnstileRequired && !turnstileToken)}>{pending ? "正在验证…" : "进入治理控制台"}<ArrowRight size={17} /></button>
          </form>
          <p className="self-claim-note">治理指令只接受当前已认证管理员输入；用户内容永远视为不可信数据。</p>
        </div>
      </section>
    </main>
  );
}

export function AdminAccessDenied({ onLogout }: { onLogout: () => void }) {
  return (
    <main className="admin-denied">
      <div className="modal-card">
        <div className="eyebrow"><ShieldCheck size={14} /> ACCESS DENIED</div>
        <h2>当前会话不是管理员账号</h2>
        <p>请先退出当前普通用户，再使用 OPERATOR 账号登录治理控制台。</p>
        <button className="button button--primary" onClick={onLogout}>退出并登录管理员</button>
        <a className="button button--ghost" href="/">返回用户界面</a>
      </div>
    </main>
  );
}
